import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getLeads } from '../redux/leadsSlice';
import { searchDeals, getSearchHistory } from '../services/api';
import DealCard from '../components/ui/DealCard';
import { LiveDealsSliderSkeleton } from '../components/ui/skeleton/LiveDealsSkeleton';
import useScreenSize from '../hooks/useScreenSize';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LiveDeals = () => {
  const { isMobile } = useScreenSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: leads = [], status, currentType } = useSelector((state) => state.leads);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  // Removed local history tracking state
  const isSearching = searchTerm.trim() !== '';

  useEffect(() => {
    if (currentType !== 'all') {
      dispatch(getLeads({ page: 1, type: "all" }));
    }
  }, [dispatch, currentType]);

  // Load search history from API
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const history = await getSearchHistory();
        // Ensure history is an array
        setSearchHistory(Array.isArray(history) ? history : []);
      } catch (error) {
        console.error('Failed to load search history:', error);
      }
    };
    fetchHistory();
  }, []);

  // Search deals via API
  useEffect(() => {
    const term = searchTerm.trim();
    if (term === '') {
      setFilteredLeads([]);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      searchDeals(term).then((results) => {
        setFilteredLeads(results || []);
        // Re-fetch history after a successful search to keep it updated if the backend saves it concurrently
        getSearchHistory().then(history => {
          if (Array.isArray(history)) setSearchHistory(history);
        });
      });
    },);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search triggered by useEffect
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleHistoryClick = (historyItem) => {
    setSearchTerm(historyItem.search_term);
  };

  return (
    <main>
      <section className='cbp_layout_header'>
        <div className='container-fluid grey-border-b-1'>
          <Header></Header>
        </div>
      </section>
      <section className='cbp_layout2'>
        <div className='container'>
          <div className="row">
            <div className="col-12 live-deals-sticky-header ps-md-2 pe-md-2 ps-3 pe-3">
              <div className={`d-flex align-items-center mt-3 mb-3 ${isSearching ? '' : 'justify-content-between'}`}>
                {!isSearching && (
                  <div className="d-flex align-items-center">
                    <button
                      onClick={() => navigate('/')}
                      className="btn btn-primary d-flex align-items-center justify-content-center border-0 shadow-sm me-3 p-2 px-md-3"
                      style={{
                        background: 'linear-gradient(to bottom, #0082CB 0%, #004E7A 100%)',
                        width: 'auto',
                        height: isMobile ? '35px' : '40px',
                        borderRadius: '48px',
                      }}
                      title="Back to Home"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      <span className="d-none d-md-inline ms-2">Back To Home</span>
                    </button>
                    <h2 className='heading-md mb-1 mt-1'>Live Deals</h2>
                  </div>
                )}
                <div className={`d-flex align-items-center ${isSearching ? 'flex-grow-1' : ''}`}>
                  {isSearching && (
                    <img src="/images/back-button.svg" className="btn-link me-2 p-0"
                      onClick={handleClearSearch} />
                  )}
                  <div className={`position-relative ${isSearching ? 'flex-grow-1' : ''}`}>
                    <form onSubmit={handleSearchSubmit}>
                      <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={() => { /* new session starts when input was cleared */ }}
                        style={{
                          paddingLeft: '40px',
                          borderRadius: isSearching ? '9999px' : undefined,
                          width: isSearching ? '100%' : undefined
                        }}
                      />
                      <img src="/images/majesticons_search-line.svg" className="search-ico position-absolute" alt="Search" style={{
                        left: isSearching ? '10px' : '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }} />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 ps-3 pe-3 ps-md-2 pe-md-2">
              {status === 'loading' && <LiveDealsSliderSkeleton />}
              {status === 'failed' && <p>Failed to load leads.</p>}
              {status === 'succeeded' && leads.length === 0 && <p>No leads available.</p>}

              {/* Search Results Grid View */}
              {searchTerm.trim() !== '' && (
                <div>
                  <div className="row p-custom-grid">
                    <div className="col-12 border-search-result">
                      <div className="d-flex justify-content-between align-items-center mb-2 m-1 search-result-header">
                        <h5>Results for <span>'{searchTerm}'</span></h5>
                        <h5>Total Search Result: <span>{filteredLeads.length.toString().padStart(2, '0')}</span></h5>
                      </div>
                      <div className="row">
                        {filteredLeads.length > 0 ? (
                          filteredLeads.map((lead, index) => (
                            <div className="col-12 col-md-6 col-lg-3 mb-3 mb-md-2 p-md-0" key={lead.name || index}>
                              <DealCard lead={lead} />
                            </div>
                          ))
                        ) : (
                          <div className="col-12 text-center py-5">
                            <p className="text-muted">No results found for '{searchTerm}'</p>
                          </div>
                        )}
                      </div>
                      <div className="row">
                        <div className="col-12 recently-viewed-list">
                          <ul className="list-unstyled">
                            {searchHistory.length > 0 ? (
                              searchHistory.slice(0, 5).map((item, index) => (
                                <li key={item.id || index} className="mb-2">
                                  <button
                                    className="btn btn-link p-0 text-start d-flex align-items-center"
                                    onClick={() => handleHistoryClick(item)}
                                    style={{ textDecoration: 'none', color: '#333333' }}
                                  >
                                    <img src="/images/carbon_recently-viewed.svg" className="me-2" alt="History" />
                                    {item.search_term} ({item.result_count} results)
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">No search history yet</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Grid View (when not searching) */}
              {status === 'succeeded' && searchTerm.trim() === '' && leads.length > 0 && (
                <div className="container p-0">
                  <div className="row">
                    {leads.map((lead, index) => (
                      <div className="col-12 col-md-6 col-lg-3 mb-3 mb-md-2 p-md-0" key={lead.deal_id || index}>
                        <DealCard lead={lead} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className='cbp_layout footer-section'>
        <div className='container'>
          <Footer></Footer>
        </div>
      </section>
      <div className='cbp_fixed_menu'>
        <div className='container-fluid'>
          <div className='row'>
            <div className='col-12 d-flex p-0'>
              <Link to="/live-deals" className="cbp_fixed_btn cbp_fixed_btn1">
                <img src='/images/deals.svg' alt='home' /> <br />
                Live deals
              </Link>
              <Link to="/become-member" className="cbp_fixed_btn cbp_fixed_btn2">
                <img src='/images/become-mem.svg' alt='home' /> <br />
                Become a member
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LiveDeals;


